import React, { useState, useEffect } from 'react';
import type { ReactElement } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import { InvestmentProfile } from '../../types/investment';
import { investmentAPI } from '../../api/investmentAPI';
import { useAuth } from '../../contexts/AuthContext';
import { useLoadingStatus } from '../../contexts/LoadingStatusContext';
import { useMemo } from 'react';

interface Props {
  open: boolean;
  profile: InvestmentProfile;
  onClose: () => void;
  onSave: (updatedProfile: InvestmentProfile) => void;
}

export const EditProfileDialog = ({
  open,
  profile,
  onClose,
  onSave
}: Props): ReactElement => {
  const [name, setName] = useState(profile.name);
  const [options, setOptions] = useState<any>(null);
  const [selectedOptions, setSelectedOptions] = useState({
    investment_profile_template: profile.investment_profile_template
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Use auth context
  const { addStatus, removeStatus } = useLoadingStatus();
  const api = useMemo(() => investmentAPI(addStatus, removeStatus), []);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoading(true);
        const data = await api.getProfileOptions();
        // console.log(data);
        setOptions(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch options');
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
  }, []);

  const handleSubmit = async () => {
    if (!name || !selectedOptions.investment_profile_template) {
      setError('Name and template are required');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      console.log('Submitting update with:', {
        id: profile.id,
        name,
        investment_profile_template: selectedOptions.investment_profile_template,
        created_at: profile.created_at
      });

      const updatedProfile = await api.updateProfile({
        id: profile.id,
        name,
        investment_profile_template: selectedOptions.investment_profile_template,
        created_at: profile.created_at
      });

      onSave(updatedProfile);
      onClose();
    } catch (err) {
      console.error('Failed to update profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (!options) return <CircularProgress />;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Investment Profile</DialogTitle>
      <DialogContent>
        {loading && <CircularProgress size={24} sx={{ position: 'absolute', top: '50%', left: '50%', mt: -1.5, ml: -1.5 }} />}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Profile Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2, mt: 2 }}
          disabled={loading}
          required
        />

        <Box sx={{ mt: 2 }}>
          {(
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Investment Profile Template</InputLabel>
                <Select
                  value={selectedOptions.investment_profile_template}
                  label="Investment Profile Template"
                  onChange={(e) => setSelectedOptions({ ...selectedOptions, investment_profile_template: e.target.value })}
                >
                  <MenuItem value="" disabled>Select Investment Profile Template</MenuItem>
                  {options.map((ipt: {
                    name: string;
                    description: string;
                    notes: string;
                    rebalance_frequency: string;
                    tax_considerations: string;
                  }) => (
                    <MenuItem key={ipt.name} value={ipt.name}>{ipt.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
